// @ts-strict-ignore
import React, { useCallback, useContext, useMemo } from 'react';
import ReactDataSheet from 'react-datasheet';
import {
  Box, Button, ButtonVariant, IconName, Tooltip,
} from '@usealloy/component-library';
import cloneDeep from 'lodash/cloneDeep';

import { selectFocusedAction } from 'flow/store/redux/selectors';
import {
  isMatrix1Dimensional, MatrixAxisOperation, getCellLabel,
  insertOneRowToMatrix,
  deleteOneRowFromMatrix,
} from 'flow/service/actions';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  MatrixModelWrapper, StyledTable, CellDiv, CellLabel, StyledMatrixCell,
  CellBox,
  StyledMatrixRowMenuLabel,
  StyledMatrixRowHeader,
} from './styles';
import type { Axis } from './AxisHeaderDropdown';
import AxisHeaderDropdown from './AxisHeaderDropdown';
import AxisConfigPopover from './AxisConfigPopover';
import AxisHeaderInput from './AxisHeaderInput';
import {
  addToAxisHeader,
  deleteLastAxisHeader,
  deleteOneHeaderRow,
  getIsDeleteDisabled,
  insertOneHeaderRow,
} from './AxisHeaderInput/helpers';
import { updateAction } from '~/dashboard/views/workflows/store/redux/slice';
import type { OnAxisLengthChangeProps, OnAxisValueChangeProps } from './AxisHeaderInput/types';
import {
  getCellColor, getShouldDefineValueRanges, handleCopyOrPasteAxisHeader, transformCellValue,
} from './helpers';
import { RuleEditorContext } from '../context';
import { updateMatrixSizeToMatchHeaders } from './AxisConfigPopover/helpers';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import type { Action, OutputValueRange } from 'flow/store/engine/types';
import { selectMatrixModelResults } from 'flow/store/redux/selectors/workflowFeatures';
import { RowHeaderDropdownMenu } from './RowHeaderDropdownMenu';

export interface GridElement extends ReactDataSheet.Cell<GridElement, number | string> {
  value: number | string;
}

type MatrixModelProps = {
  // Optional action prop - if not provided, uses the focused action from Redux
  action?: Action;
};

export default function MatrixModel({ action }: MatrixModelProps = {}): JSX.Element {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);

  // Use the provided action prop if available, otherwise fall back to focused action
  const sourceAction = action || focusedAction;
  const { publicId, matrixModel, options } = sourceAction;
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const path = `${publicId}.matrixModel`;

  const is1Dimensional = isMatrix1Dimensional(matrixModel.axis.x);
  const isYAxisDisabled = !matrixModel.dataRouteX && !is1Dimensional;

  const {
    setIsMatrixModelFocused,
  } = useContext(RuleEditorContext);

  const [yAxisConfigWidth, setYAxisConfigWidth] = React.useState<number>(200);
  const [highlightedRow, setHighlightedRow] = React.useState<number | null>(null);

  const updateYAxisConfigWidthRef = useCallback(node => {
    if (!is1Dimensional) {
      const width = node?.getBoundingClientRect().width;
      if (node && width > 200) {
        setYAxisConfigWidth(width);
      } else {
        setYAxisConfigWidth(200);
      }
    }
  }, [is1Dimensional]);

  const onAxisValueChange = useCallback(({
    axis, index, value,
  }: OnAxisValueChangeProps) => {
    const axisPath = axis.toLowerCase();
    const pathToAxisHeader = `${path}.axis.${axisPath}.axisHeader`;

    const updatedAxisHeader = [...matrixModel.axis[axisPath].axisHeader];
    updatedAxisHeader.splice(index, 1, value);

    dispatch(updateAction({
      path: pathToAxisHeader, value: updatedAxisHeader,
    }));
  }, [dispatch, matrixModel.axis, path]);

  const onAxisLengthChange = useCallback(({
    axis, op,
  }: OnAxisLengthChangeProps) => {
    const axisPath = axis.toLowerCase();

    let updatedAxisHeader;
    if (op === MatrixAxisOperation.ADD) {
      updatedAxisHeader = addToAxisHeader(matrixModel.axis[axisPath]);
    } else if (op === MatrixAxisOperation.DELETE) {
      updatedAxisHeader = deleteLastAxisHeader(matrixModel.axis[axisPath]);
    }

    const updatedMatrix = updateMatrixSizeToMatchHeaders({
      axis,
      axisSettings: {
        ...matrixModel.axis[axisPath],
        axisHeader: updatedAxisHeader,
      },
      matrix: matrixModel.matrix,
    });

    const updatedMatrixModel = {
      ...matrixModel,
      axis: {
        ...matrixModel.axis,
        [axisPath]: {
          ...matrixModel.axis[axisPath],
          axisHeader: updatedAxisHeader,
        },
      },
      matrix: updatedMatrix,
    };
    dispatch(updateAction({ path, value: updatedMatrixModel }));
  }, [dispatch, matrixModel, path]);

  const onInsertRow = useCallback(({ row, insertDirection }: { row: number; insertDirection: 'before' | 'after' }) => {
    const axisHeaderRowIndex = insertDirection === 'after' && matrixModel.axis.y.axisHeaderDataType === 'string'
      ? row - 1
      : row;
    const matrixRowIndex = insertDirection === 'after'
      ? row - 1
      : row;

    const updatedAxisHeader = insertOneHeaderRow({
      axisHeader: matrixModel.axis.y.axisHeader,
      rowIndex: axisHeaderRowIndex,
    });
    const updatedMatrix = insertOneRowToMatrix({
      rowIndex: matrixRowIndex,
      matrix: matrixModel.matrix,
    });

    const updatedMatrixModel = {
      ...matrixModel,
      axis: {
        ...matrixModel.axis,
        y: {
          ...matrixModel.axis.y,
          axisHeader: updatedAxisHeader,
        },
      },
      matrix: updatedMatrix,
    };
    dispatch(updateAction({ path, value: updatedMatrixModel }));
  }, [dispatch, matrixModel, path]);

  const onDeleteRow = useCallback((row: number) => {
    const axisHeaderRowIndex = matrixModel.axis.y.axisHeaderDataType === 'string'
      ? row - 1
      : row;
    const matrixRowIndex = row - 1;

    const updatedAxisHeader = deleteOneHeaderRow({
      axisHeader: matrixModel.axis.y.axisHeader,
      rowIndex: axisHeaderRowIndex,
    });
    const updatedMatrix = deleteOneRowFromMatrix({
      rowIndex: matrixRowIndex,
      matrix: matrixModel.matrix,
    });

    const updatedMatrixModel = {
      ...matrixModel,
      axis: {
        ...matrixModel.axis,
        y: {
          ...matrixModel.axis.y,
          axisHeader: updatedAxisHeader,
        },
      },
      matrix: updatedMatrix,
    };
    dispatch(updateAction({ path, value: updatedMatrixModel }));
  }, [dispatch, matrixModel, path]);

  const disableDeleteRow = useMemo(() => (
    getIsDeleteDisabled({
      axisHeader: matrixModel.axis.y.axisHeader,
      matrixLength: matrixModel.matrix.length,
    })
  ), [matrixModel.axis.y.axisHeader, matrixModel.matrix.length]);

  const xAxisConfig = useMemo(() => (
    <Box
      data-dd-action-name="matrix-axis-selector-x"
      style={{
        justifyContent: 'flex-start',
        paddingLeft: `${yAxisConfigWidth}px`,
      }}
    >
      <AxisHeaderDropdown
        axis="X"
        isDisabled={!inEditMode}
        action={sourceAction}
      />
      <AxisConfigPopover
        axis="X"
        isDisabled={!inEditMode}
        action={sourceAction}
      />
    </Box>
  ), [yAxisConfigWidth, inEditMode, sourceAction]);

  const yAxisConfig = useMemo(() => (
    <Box
      data-dd-action-name="matrix-axis-selector-y"
      width="max-content"
      ref={updateYAxisConfigWidthRef}
    >
      <AxisHeaderDropdown
        axis="Y"
        isDisabled={isYAxisDisabled || !inEditMode}
        action={sourceAction}
      />
      <AxisConfigPopover
        axis="Y"
        isDisabled={isYAxisDisabled || !inEditMode}
        action={sourceAction}
      />
    </Box>
  ), [isYAxisDisabled, updateYAxisConfigWidthRef, inEditMode, sourceAction]);

  /* v-v-v-v-v Rule Explainability Logic v-v-v-v-v */
  const matrixModelResultsByName = useAppSelector(selectMatrixModelResults);
  const matrixModelResults = matrixModelResultsByName[matrixModel.name];
  const explainabilityResultRow = matrixModelResults?.y_bucket;
  const explainabilityResultCol = matrixModelResults?.x_bucket;
  /* ^-^-^-^-^ Rule Explainability Logic ^-^-^-^-^ */

  const getAxisHeaders = useCallback((axisSettings, isXAxis = false) => {
    const axisValue = isXAxis ? 'X' : 'Y';
    const axisValuesToMap = (axisSettings.axisHeader.length === 1
      || axisSettings.axisHeaderDataType === 'string')
      ? axisSettings.axisHeader
      // each range is handled with index and index + 1
      : axisSettings.axisHeader.slice(0, -1);

    const axisHeaders: any[] = isXAxis
      ? [{
        value: null,
        valueViewer: () => yAxisConfig,
        disableEvents: true,
      }] : [];

    axisValuesToMap.forEach((_: any, index: number) => {
      axisHeaders.push({
        value: axisSettings.axisHeader[index],
        valueViewer: () => (
          <span
            data-dd-action-name={`matrix-axis-header-${axisValue.toLowerCase()}${index + 1}`}
            style={{ width: '-webkit-fill-available' }}
          >
            <AxisHeaderInput
              axis={axisValue}
              axisSettings={axisSettings}
              index={index}
              onChange={onAxisValueChange}
              onDelete={(axis: Axis) => onAxisLengthChange({
                axis, op: MatrixAxisOperation.DELETE,
              })}
            />
          </span>
        ),
        disableEvents: true,
      });
    });
    return axisHeaders;
  }, [onAxisLengthChange, onAxisValueChange, yAxisConfig]);

  const matrixGridToRender = useMemo(() => {
    const headerRow = isMatrix1Dimensional && getAxisHeaders(
      matrixModel.axis.x,
      true,
    );
    const headerCol = getAxisHeaders(matrixModel.axis.y);

    // used to determine whether to color matrix cells based on value range config
    const hasValueRangeConfig = getShouldDefineValueRanges(
      options.output_value_range,
      matrixModel.defineValueRanges,
    );

    const matrixGrid = headerRow ? [headerRow] : [];
    return headerCol.reduce((acc, curr, row) => {
      const arr = [];
      arr.push(curr);
      matrixModel.matrix[row].forEach((value, col) => {
        const cellLabelValue = getCellLabel(col, row);
        const shouldFadeExplainabilityCell = matrixModelResults
        && (
          row !== explainabilityResultRow
          || col !== explainabilityResultCol
        );

        arr.push({
          value,
          readOnly: !inEditMode,
          valueViewer: ({ value: cellValue }) => {
            const cellColor = getCellColor({
              cellValue,
              hasValueRangeConfig,
              outputValueRange: options.output_value_range?.jsonValue as OutputValueRange[],
            });

            return (
              <CellBox
                data-dd-action-name={`matrix-cell-${cellLabelValue}`}
                cellColor={cellColor}
                hasExplainability={Boolean(matrixModelResults)}
                shouldFadeExplainabilityCell={shouldFadeExplainabilityCell}
              >
                <CellLabel>{cellLabelValue}</CellLabel>
                <CellDiv>{cellValue}</CellDiv>
              </CellBox>
            );
          },
        });
      });
      acc.push(arr);
      return acc;
    }, matrixGrid);
  }, [
    matrixModel,
    getAxisHeaders,
    inEditMode,
    options.output_value_range,
    explainabilityResultCol,
    explainabilityResultRow,
    matrixModelResults,
  ]);

  const onCellsChanged = (changes: ReactDataSheet.CellsChangedArgs<GridElement, string>) => {
    const matrixCopy = cloneDeep(matrixModel.matrix);
    changes.forEach(({ row, col, value }) => {
      matrixCopy[row - 1][col - 1] = transformCellValue(matrixModel.matrixDataType, value);
    });
    dispatch(updateAction({ path: `${path}.matrix`, value: matrixCopy }));
  };

  return (
    <MatrixModelWrapper
      is1Dimensional={is1Dimensional}
      onFocus={() => setIsMatrixModelFocused(true)}
    >
      {!is1Dimensional && xAxisConfig}
      <Box justifyContent="flex-start" alignItems="start">
        <StyledTable
          is2Dimensional={!is1Dimensional}
          explainabilityResultRow={explainabilityResultRow}
          explainabilityResultCol={explainabilityResultCol}
          highlightedRow={highlightedRow}
        >
          <ReactDataSheet
            data={matrixGridToRender}
            cellRenderer={({
              children, row, col, ...rest
            }) => (col === 0 && row !== 0 ? (
              <StyledMatrixRowHeader
                tabIndex={0}
                onClick={event => {
                  const target = event.target as HTMLElement;
                  event.stopPropagation();
                  target.focus();
                }}
                onKeyDown={event => {
                  handleCopyOrPasteAxisHeader({
                    axis: matrixModel.axis.y,
                    cellIndex: row - 1,
                    event,
                    handleUpdateAxisHeader: updatedAxisHeader => {
                      dispatch(updateAction({ path: `${path}.axis.y.axisHeader`, value: updatedAxisHeader }));
                    },
                  });
                }}
                {...rest}
              >
                <RowHeaderDropdownMenu
                  disabled={!inEditMode}
                  disableDeleteRow={disableDeleteRow}
                  isOpen={highlightedRow === row + 1}
                  onDeleteRow={() => { onDeleteRow(row); }}
                  onInsertAfter={() => { onInsertRow({ row: row + 1, insertDirection: 'after' }); }}
                  onInsertBefore={() => { onInsertRow({ row: row - 1, insertDirection: 'before' }); }}
                  onOpenChange={isOpen => { if (!isOpen) { setHighlightedRow(null); } }}
                />
                <Tooltip content="Click to view menu" style={{ fontSize: 'var(--cl-font-size-1)', padding: 'var(--cl-space-1) var(--cl-space-2)' }}>
                  <StyledMatrixRowMenuLabel
                    disabled={!inEditMode}
                    highlighted={highlightedRow === row + 1}
                    onClick={isOpen => { setHighlightedRow(isOpen ? row + 1 : null); }}
                  >
                    {row}
                  </StyledMatrixRowMenuLabel>
                </Tooltip>
                {children}
              </StyledMatrixRowHeader>
            ) : (
              <StyledMatrixCell
                {...(row === 0 && col !== 0 && {
                  tabIndex: 0,
                  onClick: event => {
                    const target = event.target as HTMLElement;
                    event.stopPropagation();
                    target.focus();
                  },
                  onKeyDown: event => {
                    handleCopyOrPasteAxisHeader({
                      axis: matrixModel.axis.x,
                      cellIndex: col - 1,
                      event,
                      handleUpdateAxisHeader: updatedAxisHeader => {
                        dispatch(updateAction({ path: `${path}.axis.x.axisHeader`, value: updatedAxisHeader }));
                      },
                    });
                  },
                })}
                {...rest}
              >
                {children}
              </StyledMatrixCell>
            )
            )}
            onCellsChanged={onCellsChanged}
            valueRenderer={(cell: GridElement) => cell.value}
          />
        </StyledTable>
        {!is1Dimensional && inEditMode && (
          <Tooltip content="Add Column">
            <Box
              width="auto"
              data-dd-action-name="matrix-add-new-column"
            >
              <Button
                icon={IconName.Add}
                variant={ButtonVariant.tertiary}
                onClick={() => onAxisLengthChange({
                  axis: 'X', op: MatrixAxisOperation.ADD,
                })}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
      {inEditMode && (
        <Tooltip content="Add Row">
          <Box
            justifyContent="flex-start"
            width="auto"
            data-dd-action-name="matrix-add-new-row"
          >
            <Button
              icon={IconName.Add}
              variant={ButtonVariant.tertiary}
              onClick={() => onAxisLengthChange({
                axis: 'Y', op: MatrixAxisOperation.ADD,
              })}
            />
          </Box>
        </Tooltip>
      )}
    </MatrixModelWrapper>
  );
}
